"use client";

import { ShieldCheck, ClipboardCheck, Globe, Microscope, Handshake } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function Regulatory() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: itemsRef,
    isVisible: itemsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const credentials = [{
    icon: ShieldCheck,
    label: "Quality-focused Selection",
    description: "We carefully select manufacturers and products that meet internationally recognized quality expectations."
  }, {
    icon: ClipboardCheck,
    label: "Market Access Assessment",
    description: "We provide preliminary market access and documentation assessments to support international commercialization."
  }, {
    icon: Globe,
    label: "Global Quality Standards",
    description: "We work with globally recognized manufacturers and align our collaborations with international quality expectations."
  }, {
    icon: Microscope,
    label: "Product Evaluation",
    description: "We evaluate clinical value, product performance, and commercial potential to support informed business decisions."
  }, {
    icon: Handshake,
    label: "Long-term Partnership",
    description: "We believe sustainable partnerships are built on professionalism, transparency, and shared growth."
  }];
  return <section data-section-id="4418" id="regulatorycertifications" className="relative overflow-hidden bg-background text-foreground py-16 md:py-28 lg:py-32">
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 md:h-96 md:w-96 opacity-25 blur-2xl" style={{
      backgroundColor: "var(--primary)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs tracking-[0.02em] uppercase mb-4 text-primary font-semibold">
            Trusted Quality Assurance
          </p>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.2] tracking-normal text-foreground font-bold">Professional Evaluation for Global Market Readiness</h2>
          <p className="mt-6 text-lg md:text-xl leading-[1.6] font-default text-muted-foreground">We help partners prepare for international market development through quality-focused evaluation, product assessment, and strategic guidance.</p>
        </div>

        <div ref={itemsRef} className="relative mt-20 md:mt-24">
          <Separator className="hidden md:block absolute left-0 right-0 top-[60px] bg-border" />
          <Separator orientation="vertical" className="md:hidden absolute left-1/2 top-0 bottom-0 bg-border" />

          <div className="relative grid grid-cols-2 md:grid-cols-5 gap-y-12 gap-x-6 md:gap-x-4">
            {credentials.map((item, i) => {
            const Icon = item.icon;
            return <div data-index={i} key={item.label} className={`relative flex flex-col items-center text-center px-2 transition-all duration-700 ease-out ${itemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {i < credentials.length - 1 && <Separator orientation="vertical" className="hidden md:block absolute right-0 top-8 bottom-8 bg-border/50" />}

                  <Card className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-lg shadow-md bg-white hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-0 flex items-center justify-center">
                      <Icon strokeWidth={1.5} className="w-10 h-10 md:w-12 md:h-12 text-primary" aria-hidden="true" />
                    </CardContent>
                  </Card>

                  <h3 className="mt-6 font-default text-[11px] tracking-[0.1em] uppercase text-foreground font-bold">
                    {item.label}
                  </h3>
                  <p className="mt-2 text-sm leading-[1.6] max-w-[180px] font-default text-muted-foreground">
                    {item.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}